import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deficiencyEntriesApi,
  type DeficiencyEntry,
  type ListFilter,
  type CreateEntryInput,
  type UpdateEntryPatch,
} from '../../api/deficiency-entries';
import type { AutosaveState } from '../templates/AutosaveIndicator';

interface LibraryState {
  entries: DeficiencyEntry[];
  loading: boolean;
  error: string | null;
  saveState: AutosaveState;
}

/**
 * Loads the inspector's full library (no filter) once and exposes local
 * CRUD helpers. Mutations are optimistic: state is patched in-place, then
 * replaced with the server response. `filter` stays client-side because the
 * library is small enough (≤ few hundred entries) to filter without a round
 * trip, and this keeps the page snappy when toggling sections.
 */
export function useDeficiencyLibrary() {
  const [state, setState] = useState<LibraryState>({
    entries: [],
    loading: true,
    error: null,
    saveState: 'idle',
  });

  const pending = useRef(0);
  const savedTimeout = useRef<number | null>(null);

  const load = useCallback(async (filter?: ListFilter) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const entries = await deficiencyEntriesApi.list(filter);
      setState({ entries, loading: false, error: null, saveState: 'idle' });
    } catch (e) {
      setState((s) => ({
        ...s,
        loading: false,
        error: (e as Error).message ?? 'Failed to load library',
      }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markSaving = useCallback(() => {
    pending.current += 1;
    if (savedTimeout.current) window.clearTimeout(savedTimeout.current);
    setState((s) => ({ ...s, saveState: 'saving' }));
  }, []);

  const markDone = useCallback((ok: boolean, errorMsg?: string) => {
    pending.current = Math.max(0, pending.current - 1);
    setState((s) => ({
      ...s,
      saveState: ok ? (pending.current === 0 ? 'saved' : 'saving') : 'error',
      error: ok ? null : errorMsg ?? s.error,
    }));
    if (ok && pending.current === 0) {
      savedTimeout.current = window.setTimeout(
        () => setState((s) => (s.saveState === 'saved' ? { ...s, saveState: 'idle' } : s)),
        2000,
      );
    }
  }, []);

  const create = useCallback(async (input: CreateEntryInput): Promise<DeficiencyEntry | null> => {
    markSaving();
    try {
      const entry = await deficiencyEntriesApi.create(input);
      setState((s) => ({ ...s, entries: [...s.entries, entry] }));
      markDone(true);
      return entry;
    } catch (e) {
      markDone(false, (e as Error).message);
      return null;
    }
  }, [markSaving, markDone]);

  const update = useCallback(async (id: string, patch: UpdateEntryPatch): Promise<DeficiencyEntry | null> => {
    markSaving();
    // Optimistic
    setState((s) => ({
      ...s,
      entries: s.entries.map((e) => (e.id === id ? { ...e, ...patchToEntry(patch) } : e)),
    }));
    try {
      const entry = await deficiencyEntriesApi.update(id, patch);
      setState((s) => ({
        ...s,
        entries: s.entries.map((e) => (e.id === id ? entry : e)),
      }));
      markDone(true);
      return entry;
    } catch (e) {
      markDone(false, (e as Error).message);
      await load();
      return null;
    }
  }, [markSaving, markDone, load]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    markSaving();
    const prev = state.entries;
    setState((s) => ({ ...s, entries: s.entries.filter((e) => e.id !== id) }));
    try {
      await deficiencyEntriesApi.remove(id);
      markDone(true);
      return true;
    } catch (e) {
      setState((s) => ({ ...s, entries: prev }));
      markDone(false, (e as Error).message);
      return false;
    }
  }, [markSaving, markDone, state.entries]);

  const reorder = useCallback(async (sectionName: string, ids: string[]): Promise<boolean> => {
    markSaving();
    setState((s) => {
      const byId = new Map(s.entries.map((e) => [e.id, e]));
      const reordered = ids
        .map((id, i) => {
          const e = byId.get(id);
          return e ? { ...e, orderIndex: i } : null;
        })
        .filter((e): e is DeficiencyEntry => e !== null);
      const untouched = s.entries.filter((e) => !ids.includes(e.id));
      return { ...s, entries: [...reordered, ...untouched] };
    });
    try {
      await deficiencyEntriesApi.reorder(sectionName, ids);
      markDone(true);
      return true;
    } catch (e) {
      markDone(false, (e as Error).message);
      await load();
      return false;
    }
  }, [markSaving, markDone, load]);

  return {
    ...state,
    reload: load,
    create,
    update,
    remove,
    reorder,
  };
}

function patchToEntry(patch: UpdateEntryPatch): Partial<DeficiencyEntry> {
  const out: Partial<DeficiencyEntry> = {};
  if (patch.title !== undefined) out.title = patch.title;
  if (patch.description !== undefined) out.description = patch.description ?? null;
  if (patch.severity !== undefined) out.severity = patch.severity ?? null;
  if (patch.section_name !== undefined) out.sectionName = patch.section_name;
  if (patch.component_name !== undefined) out.componentName = patch.component_name ?? null;
  if (patch.trade_tag !== undefined) out.tradeTag = patch.trade_tag ?? null;
  if (patch.order_index !== undefined) out.orderIndex = patch.order_index;
  return out;
}
